import { cn } from '@/lib/utils'
import { aiEmployeeLabel, resolveAIEmployee, type AIEmployeeHue } from '@/lib/ai-employees'

const SIZE: Record<20 | 32 | 40, string> = {
  20: 'h-5 w-5 rounded-md text-xs',
  32: 'h-8 w-8 rounded-lg text-xs',
  40: 'h-10 w-10 rounded-xl text-sm',
}

/** Monogram square tints by connector hue; the navy ink keeps text contrast on every tint. */
const HUE: Record<AIEmployeeHue, string> = {
  1: 'bg-orch-1/15 text-foreground ring-orch-1/60',
  2: 'bg-orch-2/15 text-foreground ring-orch-2/60',
  3: 'bg-orch-3/15 text-foreground ring-orch-3/60',
  4: 'bg-orch-4/15 text-foreground ring-orch-4/60',
  rail: 'bg-primary text-primary-foreground ring-primary',
}

export interface AIEmployeeMarkProps {
  /** An AI Employee id, an Auto id (A0 to A6) or a voice agent id (V1, V2). */
  id: string
  size?: 20 | 32 | 40
  /** Adds "Situation Analyst · AI Employee" next to the mark. */
  showName?: boolean
  className?: string
}

/** The AI Employee monogram (plans/00 B20: monograms only, never personal names or photos). */
export function AIEmployeeMark({ id, size = 32, showName = false, className }: AIEmployeeMarkProps) {
  const employee = resolveAIEmployee(id)
  if (!employee) return null
  const label = aiEmployeeLabel(employee)
  const mark = (
    <span
      role={showName ? undefined : 'img'}
      aria-label={showName ? undefined : label}
      aria-hidden={showName ? true : undefined}
      title={showName ? undefined : label}
      className={cn(
        'inline-flex shrink-0 items-center justify-center font-semibold tracking-wide ring-1',
        SIZE[size],
        HUE[employee.hue],
        !showName && className
      )}
    >
      {employee.monogram}
    </span>
  )
  if (!showName) return mark
  return (
    <span className={cn('inline-flex min-w-0 items-center gap-2', className)}>
      {mark}
      <span className='truncate text-sm text-foreground'>
        <span className='font-medium'>{employee.name}</span>
        <span className='text-muted-foreground'> · AI Employee</span>
      </span>
    </span>
  )
}
